import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Dictionary, Plural } from "./types";
import { en } from "./locales/en";
import { es } from "./locales/es";
import { uk } from "./locales/uk";

export type Locale = "en" | "es" | "uk";

export const LOCALES: { code: Locale; label: string; english: string }[] = [
  { code: "en", label: "English", english: "English" },
  { code: "es", label: "Español", english: "Spanish" },
  { code: "uk", label: "Українська", english: "Ukrainian" },
];

const DICTIONARIES: Record<Locale, Dictionary> = { en, es, uk };
const STORAGE_KEY = "retrospecter-locale";
export const DEFAULT_LOCALE: Locale = "en";

function isLocale(value: unknown): value is Locale {
  return value === "en" || value === "es" || value === "uk";
}

/** Stored choice first, then the browser's languages, then English. */
export function detectLocale(
  stored: string | null,
  languages: readonly string[] = []
): Locale {
  if (isLocale(stored)) return stored;

  for (const language of languages) {
    const base = language.toLowerCase().split("-")[0];
    if (isLocale(base)) return base;
  }

  return DEFAULT_LOCALE;
}

function readStoredLocale(): string | null {
  if (typeof localStorage === "undefined") return null;
  return localStorage.getItem(STORAGE_KEY);
}

type Params = Record<string, string | number>;

/** Replaces `{name}` placeholders. Unknown placeholders are left untouched. */
export function interpolate(template: string, params?: Params): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in params ? String(params[key]) : match
  );
}

/**
 * Picks the right plural form for `count`.
 *
 * Intl.PluralRules does the language-specific work — Ukrainian resolves to
 * one/few/many, which hand-written rules routinely get wrong.
 */
export function selectPlural(
  forms: Plural,
  count: number,
  locale: Locale
): string {
  const category = new Intl.PluralRules(locale).select(count);
  return forms[category as keyof Plural] ?? forms.other;
}

/**
 * Finds a column's translated title or hint.
 *
 * Prefers the board's own template, because the same English column title can
 * appear in more than one format with different wording ("More of" reads
 * differently in KALM and Starfish). Falls back to scanning every template so a
 * board still reads sensibly when its format is unknown.
 */
function lookupColumn(
  dictionary: Dictionary,
  field: "columns" | "hints",
  englishTitle: string,
  templateName?: string
): string | undefined {
  const scoped = templateName ? dictionary.templates[templateName] : undefined;
  if (scoped?.[field][englishTitle]) return scoped[field][englishTitle];

  for (const template of Object.values(dictionary.templates)) {
    const translated = template[field][englishTitle];
    if (translated) return translated;
  }

  return undefined;
}

/** Dot path into the dictionary, e.g. "board.joinPrompt". */
function lookup(dictionary: Dictionary, path: string): unknown {
  return path
    .split(".")
    .reduce<unknown>(
      (node, key) =>
        node && typeof node === "object"
          ? (node as Record<string, unknown>)[key]
          : undefined,
      dictionary
    );
}

export interface I18n {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  /** Translates a key, interpolating `{params}`. Falls back to English. */
  t: (path: string, params?: Params) => string;
  /** Translates a pluralised key using `count`. */
  plural: (path: string, count: number, params?: Params) => string;
  /** Locale-aware date, for anything shown to the user. */
  formatDate: (iso: string) => string;
  /** Translates a seeded template's name/description/column titles. */
  templateName: (englishName: string) => string;
  templateDescription: (englishName: string, fallback: string | null) => string;
  /**
   * Translates a column title. `templateName` scopes the lookup: "More of"
   * exists in both KALM and Starfish with different wording, so an unscoped
   * search would return whichever template happens to come first.
   */
  columnTitle: (englishTitle: string, templateName?: string) => string;
  /** Translates a column's guidance, falling back to the stored English. */
  columnHint: (
    englishTitle: string,
    fallback: string | null,
    templateName?: string
  ) => string;
}

const I18nContext = createContext<I18n | null>(null);

export function I18nProvider({
  children,
  initialLocale,
}: {
  children: ReactNode;
  initialLocale?: Locale;
}) {
  const [locale, setLocaleState] = useState<Locale>(
    () =>
      initialLocale ??
      detectLocale(
        readStoredLocale(),
        typeof navigator === "undefined" ? [] : navigator.languages ?? [navigator.language]
      )
  );

  useEffect(() => {
    document.documentElement.setAttribute("lang", locale);
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // A blocked localStorage should not stop the language changing.
    }
  }, []);

  const value = useMemo<I18n>(() => {
    const dictionary = DICTIONARIES[locale];

    const resolve = (path: string): unknown =>
      lookup(dictionary, path) ?? lookup(en, path);

    const t = (path: string, params?: Params) => {
      const entry = resolve(path);
      // A missing key shows the key itself: loud in review, harmless in prod.
      if (typeof entry !== "string") return path;
      return interpolate(entry, params);
    };

    const plural = (path: string, count: number, params?: Params) => {
      const entry = resolve(path);
      if (!entry || typeof entry !== "object") return path;
      const form = selectPlural(entry as Plural, count, locale);
      return interpolate(form, { count, ...params });
    };

    /** Seeded templates are stored in English; translate them for display. */
    const templateStrings = (englishName: string) =>
      dictionary.templates[englishName] ?? en.templates[englishName];

    return {
      locale,
      setLocale,
      t,
      plural,
      formatDate: (iso: string) =>
        new Intl.DateTimeFormat(locale, {
          year: "numeric",
          month: "short",
          day: "numeric",
        }).format(new Date(iso)),
      templateName: (englishName) => templateStrings(englishName)?.name ?? englishName,
      templateDescription: (englishName, fallback) =>
        templateStrings(englishName)?.description ?? fallback ?? "",
      columnTitle: (englishTitle, templateName) =>
        lookupColumn(dictionary, "columns", englishTitle, templateName) ??
        englishTitle,
      columnHint: (englishTitle, fallback, templateName) =>
        lookupColumn(dictionary, "hints", englishTitle, templateName) ??
        // A custom column keeps whatever guidance the database holds.
        fallback ??
        "",
    };
  }, [locale, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useTranslation(): I18n {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useTranslation must be used inside <I18nProvider>");
  }
  return context;
}
